import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend
);

const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/**
 * Parses "dd-mm-yyyy" → "12 May" style label.
 * Also handles "yyyy-mm-dd". Falls back to raw string.
 */
function formatDateLabel(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  let dd, mm;
  if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(s)) {
    [dd, mm] = s.split("-").map(Number);
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const p = s.split("-");
    dd = Number(p[2]);
    mm = Number(p[1]);
  } else {
    return s;
  }
  return `${dd} ${MONTH_ABBR[mm - 1] || ""}`;
}

/**
 * Line chart for insights daily trend data (Last 30 days).
 * X-axis shows day-of-month numbers; ticks rendered every ~5 days for readability.
 * Full date shown in tooltip on hover.
 *
 * @param {string[]} labels - Raw date strings from API ("dd-mm-yyyy")
 * @param {number[]} values - Y-axis values
 * @param {string} [datasetLabel] - Label for tooltip
 */
export function InsightsTrendLineChart({ labels = [], values = [], datasetLabel = "Trend" }) {
  const chartRef = useRef();

  // Format raw date strings to "dd MMM" for x-axis display (e.g. "12 May")
  const formattedLabels = labels.map(formatDateLabel);

  const data = {
    labels: formattedLabels,
    datasets: [
      {
        label: datasetLabel,
        fill: true,
        lineTension: 0.2,
        backgroundColor: "rgba(162, 150, 234, 0.2)",
        borderColor: "rgb(162, 150, 234)",
        pointBorderColor: "#253BA2",
        pointBackgroundColor: "#fff",
        pointBorderWidth: 1,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: "rgba(203, 232, 254, 0.5)",
        pointHoverBorderColor: "rgba(167, 37, 37, 0.67)",
        pointHoverBorderWidth: 2,
        pointRadius: 2,
        pointHitRadius: 10,
        data: values,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: { display: false },
      legend: { display: false },
      title: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            if (!items.length) return "";
            const idx = items[0].dataIndex;
            // Show full formatted date in tooltip
            return formattedLabels[idx] || labels[idx] || "";
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          autoSkip: false,
          callback: (val, idx) => {
            // Show every 3rd data point label
            return idx % 3 === 0 ? formattedLabels[idx] : "";
          },
          font: { size: 11 },
        },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: {
          beginAtZero: true,
          callback: (v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v,
        },
      },
    },
  };

  if (!formattedLabels.length || !values.length) return null;

  return <Line options={options} data={data} ref={chartRef} />;
}
